import { ClearRounded, DeleteOutlineRounded, FileDownloadRounded, RefreshRounded, UploadRounded } from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import ExcelJS from "exceljs";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { useEffect, useMemo, useState } from "react";

const ID_KEY_OPTIONS = {
	style_id: { key: "style_id", name: "style_id", headers: ["style_id"] },
	option_id: { key: "option_id", name: "option_id", headers: ["option_id"] },
	style_ref_no: { key: "style_ref_no", name: "style_ref_no", headers: ["style_ref_no"] },
};

const COLOR_LEVEL_ID_TYPES = {
	option_id: {
		missingDetailKey: "missing_option_ids",
		conflictIdentifiersKey: "option_ids",
		notFoundLabel: "option_id",
	},
	style_ref_no: {
		missingDetailKey: "missing_style_ref_nos",
		conflictIdentifiersKey: "style_ref_nos",
		notFoundLabel: "style_ref_no",
	},
};

const normalizeHeader = (value) =>
	String(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "");

const parseCsvLine = (line) => {
	const values = [];
	let current = "";
	let inQuotes = false;

	for (let index = 0; index < line.length; index += 1) {
		const char = line[index];
		const nextChar = line[index + 1];

		if (char === '"') {
			if (inQuotes && nextChar === '"') {
				current += '"';
				index += 1;
			} else {
				inQuotes = !inQuotes;
			}
			continue;
		}

		if (char === "," && !inQuotes) {
			values.push(current.trim());
			current = "";
			continue;
		}

		current += char;
	}

	values.push(current.trim());
	return values;
};

const formatCellValue = (value) => {
	if (value == null) return "";
	if (value instanceof Date && !Number.isNaN(value.getTime())) {
		const pad = (item) => String(item).padStart(2, "0");
		return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())} ${pad(
			value.getHours(),
		)}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`;
	}
	if (typeof value === "object") {
		if (value?.text != null) return String(value.text).trim();
		if (value?.result != null) return String(value.result).trim();
		if (value?.richText?.length)
			return value.richText
				.map((part) => part.text)
				.join("")
				.trim();
	}
	return String(value).trim();
};

const formatMrpValue = (value) => {
	const rawValue = formatCellValue(value);
	if (rawValue === "") return "";

	const numericValue = Number(rawValue);
	return Number.isFinite(numericValue) ? numericValue : rawValue;
};

const normalizeKeyValue = (value) => String(value ?? "").trim();

const UploadModal = ({ modal, setModal, callbackFun, canPublish = false }) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [fileName, setFileName] = useState("");
	const [headers, setHeaders] = useState([]);
	const [sourceRows, setSourceRows] = useState([]);
	const [idKeyType, setIdKeyType] = useState(ID_KEY_OPTIONS.style_id.key);
	const [idColumnIndex, setIdColumnIndex] = useState("");
	const [mrpColumnIndex, setMrpColumnIndex] = useState("");
	const [markAsPublished, setMarkAsPublished] = useState(false);
	const [previewRows, setPreviewRows] = useState([]);
	const [rowFeedback, setRowFeedback] = useState({});

	const idKeyLabel = ID_KEY_OPTIONS[idKeyType]?.name || "style_id";

	const resetState = () => {
		setLoading(false);
		setError("");
		setFileName("");
		setHeaders([]);
		setSourceRows([]);
		setIdColumnIndex("");
		setMrpColumnIndex("");
		setMarkAsPublished(false);
		setPreviewRows([]);
		setRowFeedback({});
	};

	const handleClose = () => {
		resetState();
		setIdKeyType(ID_KEY_OPTIONS.style_id.key);
		setModal(false);
	};

	const parseCsvFile = async (file) => {
		const text = await file.text();
		const lines = text.split(/\r?\n/).filter((line) => line.trim());
		if (!lines.length) return { headers: [], rows: [] };

		const parsedHeaders = parseCsvLine(lines[0]).map((value, index) => value || `Column ${index + 1}`);
		const parsedRows = lines.slice(1).map((line) => parseCsvLine(line));

		return { headers: parsedHeaders, rows: parsedRows };
	};

	const parseExcelFile = async (file) => {
		const workbook = new ExcelJS.Workbook();
		const buffer = await file.arrayBuffer();
		await workbook.xlsx.load(buffer);

		const worksheet = workbook.worksheets[0];
		if (!worksheet) return { headers: [], rows: [] };

		const parsedHeaders = worksheet
			.getRow(1)
			.values.slice(1)
			.map((value, index) => formatCellValue(value) || `Column ${index + 1}`);

		const parsedRows = [];
		worksheet.eachRow((row, rowNumber) => {
			if (rowNumber === 1) return;
			parsedRows.push(row.values.slice(1));
		});

		return { headers: parsedHeaders, rows: parsedRows };
	};

	const getDefaultIndex = (list, targetKeys) => list.findIndex((header) => targetKeys.includes(normalizeHeader(header)));

	const buildMappedRows = (
		rows = sourceRows,
		idColumn = idColumnIndex,
		mrpColumn = mrpColumnIndex,
		keyType = idKeyType,
	) => {
		const keyLabel = ID_KEY_OPTIONS[keyType]?.name || "style_id";

		if (idColumn === "" || mrpColumn === "") {
			throw new Error(`Select ${keyLabel} and final_mrp columns first.`);
		}

		if (idColumn === mrpColumn) {
			throw new Error(`${keyLabel} and final_mrp must be mapped to different columns.`);
		}

		const idIndex = Number(idColumn);
		const mrpIndex = Number(mrpColumn);

		const groupedRows = rows.reduce((acc, row, index) => {
			const keyValue = formatCellValue(row?.[idIndex]);
			const finalMrp = formatMrpValue(row?.[mrpIndex]);
			if (keyValue === "" || keyValue == null) {
				return acc;
			}

			if (!acc[keyValue]) {
				acc[keyValue] = {
					__rowId: `mrp-upload-row-${index}`,
					[keyLabel]: keyValue,
					final_mrp: finalMrp,
					__status: "Pending",
					__note: "",
					__keyValue: keyValue,
					__count: 0,
					__mrs: new Set(),
				};
			}
			acc[keyValue].__count += 1;
			acc[keyValue].__mrs.add(String(finalMrp));
			return acc;
		}, {});

		return Object.values(groupedRows).map((row) => {
			if (row[keyLabel] == null || row[keyLabel] === "") {
				return { ...row, __isValid: false, __status: "Invalid", __note: `Missing ${keyLabel}` };
			}

			if (row.final_mrp === "" || row.final_mrp == null || Number.isNaN(Number(row.final_mrp))) {
				return { ...row, __isValid: false, __status: "Invalid", __note: "Missing or invalid final_mrp" };
			}

			if (row.__mrs.size > 1) {
				return {
					...row,
					__isValid: false,
					__status: "Duplicate",
					__note: `${keyLabel} has multiple different MRPs in the file`,
				};
			}

			return {
				...row,
				__isValid: true,
				__status: "Valid",
				__note: row.__count > 1 ? `Merged ${row.__count} rows with the same MRP` : "Ready to upload",
			};
		});
	};

	const detectColumnsForKeyType = (parsedHeaders, keyType) => {
		const keyHeaders = ID_KEY_OPTIONS[keyType]?.headers || ["style_id"];
		const idIndex = getDefaultIndex(parsedHeaders, keyHeaders);
		const mrpIndex = getDefaultIndex(parsedHeaders, ["final_mrp"]);
		return {
			idIndex: idIndex >= 0 ? String(idIndex) : "",
			mrpIndex: mrpIndex >= 0 ? String(mrpIndex) : "",
		};
	};

	const handleFileChange = async (event) => {
		const selectedFile = event.target.files?.[0];
		event.target.value = "";

		if (!selectedFile) return;

		setLoading(true);
		setError("");

		try {
			const isCsv = selectedFile.name.toLowerCase().endsWith(".csv");
			const parsedData = isCsv ? await parseCsvFile(selectedFile) : await parseExcelFile(selectedFile);
			const { idIndex, mrpIndex } = detectColumnsForKeyType(parsedData.headers, idKeyType);

			setFileName(selectedFile.name);
			setHeaders(parsedData.headers);
			setSourceRows(parsedData.rows);
			setIdColumnIndex(idIndex);
			setMrpColumnIndex(mrpIndex);
			setRowFeedback({});
			if (idIndex !== "" && mrpIndex !== "") {
				setPreviewRows(buildMappedRows(parsedData.rows, idIndex, mrpIndex, idKeyType));
			} else {
				setPreviewRows([]);
			}
		} catch (uploadError) {
			resetState();
			setError(uploadError.message || "Unable to read the selected file.");
		} finally {
			setLoading(false);
		}
	};

	const handleIdKeyTypeChange = (nextKeyType) => {
		setIdKeyType(nextKeyType);
		setRowFeedback({});
		setError("");

		if (!headers.length) {
			setIdColumnIndex("");
			setPreviewRows([]);
			return;
		}

		const { idIndex, mrpIndex } = detectColumnsForKeyType(headers, nextKeyType);
		setIdColumnIndex(idIndex);
		if (mrpColumnIndex === "" && mrpIndex !== "") setMrpColumnIndex(mrpIndex);

		const effectiveMrp = mrpColumnIndex !== "" ? mrpColumnIndex : mrpIndex;
		if (idIndex !== "" && effectiveMrp !== "" && sourceRows.length) {
			try {
				setPreviewRows(buildMappedRows(sourceRows, idIndex, effectiveMrp, nextKeyType));
			} catch (previewError) {
				setPreviewRows([]);
				setError(previewError.message || "Unable to preview file.");
			}
		} else {
			setPreviewRows([]);
		}
	};

	const handlePreview = async () => {
		setLoading(true);
		setError("");

		try {
			const nextRows = buildMappedRows(sourceRows);
			setPreviewRows(nextRows);
			setRowFeedback({});
		} catch (previewError) {
			setError(previewError.message || "Unable to preview file.");
			setPreviewRows([]);
		} finally {
			setLoading(false);
		}
	};

	const handleRefreshRows = async () => {
		if (!sourceRows.length) return;
		setRowFeedback({});
		await handlePreview();
	};

	const handleDownloadFormat = async () => {
		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet("MRP Upload");
		const sampleValue =
			idKeyType === "option_id" ? "OPT_001" : idKeyType === "style_ref_no" ? "REF_001" : "STYLE_001";
		worksheet.addRow([idKeyLabel, "final_mrp"]);
		worksheet.addRow([sampleValue, 2999]);

		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.download = `mrp_sheet_upload_format_${idKeyLabel}.xlsx`;
		link.click();
		URL.revokeObjectURL(link.href);
	};

	const handleFormSubmit = async (event) => {
		event.preventDefault();

		if (typeof callbackFun !== "function") return;

		if (displayPreviewRows.some((row) => row.__isValid === false && row.__status === "Duplicate")) {
			setError(`Each ${idKeyLabel} must have the same MRP across all occurrences.`);
			return;
		}

		setLoading(true);
		try {
			const uniqueRows = displayPreviewRows
				.filter((row) => row.__isValid !== false)
				.map((row) =>
					idKeyType === "style_id"
						? { style_id: row.style_id, final_mrp: row.final_mrp }
						: { [idKeyType]: row[idKeyType], final_mrp: row.final_mrp },
				);

			const uploadResult = await callbackFun(uniqueRows, { markAsPublished, idType: idKeyType });
			const missingStyleIds = uploadResult?.result?.missing_style_ids || [];
			const rejectedStyleIds = uploadResult?.result?.rejected_style_ids || [];
			const inwardedStyleIds = uploadResult?.result?.inwarded_style_ids || [];
			const inwardedOptionIds = uploadResult?.result?.inwarded_option_ids || [];
			const inwardedStyleRefNos = uploadResult?.result?.inwarded_style_ref_nos || [];
			const conflictingStyles = uploadResult?.result?.conflicting_styles || [];
			const colorLevelConfig = COLOR_LEVEL_ID_TYPES[idKeyType];
			const missingColorLevelIds = colorLevelConfig
				? uploadResult?.result?.[colorLevelConfig.missingDetailKey] || []
				: [];
			const hasFeedback =
				missingStyleIds.length > 0 ||
				rejectedStyleIds.length > 0 ||
				inwardedStyleIds.length > 0 ||
				inwardedOptionIds.length > 0 ||
				inwardedStyleRefNos.length > 0 ||
				missingColorLevelIds.length > 0 ||
				conflictingStyles.length > 0;

			if (hasFeedback) {
				const nextFeedback = {};

				if (colorLevelConfig) {
					const missingSet = new Set(missingColorLevelIds.map((value) => normalizeKeyValue(value)));
					const inwardedSet = new Set(
						(idKeyType === "option_id" ? inwardedOptionIds : inwardedStyleRefNos).map((value) =>
							normalizeKeyValue(value),
						),
					);
					const conflictIdentifierMap = {};
					conflictingStyles.forEach((conflict) => {
						(conflict[colorLevelConfig.conflictIdentifiersKey] || []).forEach((identifier) => {
							conflictIdentifierMap[normalizeKeyValue(identifier)] = conflict;
						});
					});

					previewRows.forEach((row) => {
						const identifier = normalizeKeyValue(row[idKeyLabel]);
						if (missingSet.has(identifier)) {
							nextFeedback[identifier] = {
								__isValid: false,
								__status: "Missing",
								__note: `${identifier}: ${colorLevelConfig.notFoundLabel} not found`,
							};
						}
						if (conflictIdentifierMap[identifier]) {
							const conflict = conflictIdentifierMap[identifier];
							nextFeedback[identifier] = {
								__isValid: false,
								__status: "Conflict",
								__note: `Same style ${conflict.style_id} has different MRPs (${(conflict.mrps || []).join(", ")}) across ${colorLevelConfig.notFoundLabel}s`,
							};
						}
						if (inwardedSet.has(identifier)) {
							nextFeedback[identifier] = {
								__isValid: false,
								__status: "Already Inwarded",
								__note: "Already Inwarded",
							};
						}
					});
				} else {
					const missingSet = new Set(missingStyleIds.map((value) => normalizeKeyValue(value)));
					const rejectedSet = new Set(rejectedStyleIds.map((value) => normalizeKeyValue(value)));
					const inwardedSet = new Set(inwardedStyleIds.map((value) => normalizeKeyValue(value)));

					previewRows.forEach((row) => {
						const styleId = normalizeKeyValue(row.style_id);
						if (missingSet.has(styleId)) {
							nextFeedback[styleId] = {
								__isValid: false,
								__status: "Missing",
								__note: `${styleId}: Missing style_id in style master`,
							};
						}
						if (rejectedSet.has(styleId)) {
							nextFeedback[styleId] = {
								__isValid: false,
								__status: "Rejected",
								__note: `${styleId}: Rejected styles cannot be updated`,
							};
						}
						if (inwardedSet.has(styleId)) {
							nextFeedback[styleId] = {
								__isValid: false,
								__status: "Already Inwarded",
								__note: "Already Inwarded",
							};
						}
					});
				}

				setRowFeedback(nextFeedback);
				setError(uploadResult?.message || "Upload validation failed.");
				return;
			}

			if (uploadResult?.success === false) {
				setError(uploadResult?.message || "Upload failed.");
				return;
			}

			if (uploadResult !== false) {
				handleClose();
			}
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteRow = (rowId) => {
		setPreviewRows((prev) => {
			const nextRows = prev.filter((row) => row.__rowId !== rowId);
			const removedRow = prev.find((row) => row.__rowId === rowId);
			const removedKey = normalizeKeyValue(removedRow?.[idKeyLabel]);

			if (removedKey) {
				setRowFeedback((current) => {
					const nextFeedback = { ...current };
					delete nextFeedback[removedKey];
					return nextFeedback;
				});
			}

			return nextRows;
		});
	};

	const displayPreviewRows = useMemo(
		() =>
			previewRows.map((row) => {
				const feedback = rowFeedback[normalizeKeyValue(row[idKeyLabel])];
				return feedback ? { ...row, ...feedback } : row;
			}),
		[previewRows, rowFeedback, idKeyLabel],
	);

	const canMarkAsPublished =
		canPublish && displayPreviewRows.length > 0 && displayPreviewRows.every((row) => row.__isValid !== false);

	useEffect(() => {
		if (!canMarkAsPublished && markAsPublished) {
			setMarkAsPublished(false);
		}
	}, [canMarkAsPublished, markAsPublished]);

	const uploadPreviewColumns = useMemo(
		() => [
			{
				key: "__upload_status",
				name: "UPLOAD STATUS",
				headerAlign: "center",
				align: "center",
				minWidth: 160,
				maxWidth: 180,
				valueGetter: ({ row }) =>
					row.__isValid === false ? row.__status || row.status || "Invalid" : row.__status || row.status || "Valid",
				renderCell: ({ row }) => (
					<Box color={row.__isValid === false ? "error.main" : "success.main"} fontWeight={600}>
						{row.__isValid === false ? row.__status || row.status || "Invalid" : row.__status || row.status || "Valid"}
					</Box>
				),
			},
			{
				key: idKeyLabel,
				name: idKeyLabel.toUpperCase(),
				headerAlign: "center",
				align: "center",
				minWidth: 150,
			},
			{
				key: "final_mrp",
				name: "FINAL_MRP",
				headerAlign: "center",
				align: "center",
				minWidth: 150,
			},
			{
				key: "__note",
				name: "NOTE",
				headerAlign: "center",
				align: "center",
				minWidth: 260,
				valueGetter: ({ row }) => row.__note || row.note || "",
				renderCell: ({ row }) => (
					<Typography
						variant="body2"
						color={row.__isValid === false ? "error.main" : "text.secondary"}
						sx={{ width: "100%" }}
						textAlign="center">
						{row.__note || row.note || ""}
					</Typography>
				),
			},
			{
				key: "__delete",
				name: "DELETE",
				headerAlign: "center",
				align: "center",
				minWidth: 90,
				maxWidth: 90,
				valueGetter: () => "",
				renderCell: ({ row }) => (
					<IconButton
						size="small"
						color="error"
						onClick={(event) => {
							event.stopPropagation();
							handleDeleteRow(row.__rowId);
						}}>
						<DeleteOutlineRounded fontSize="small" />
					</IconButton>
				),
			},
		],
		[idKeyLabel],
	);

	const validCount = [
		...new Set(displayPreviewRows.filter((row) => row.__isValid !== false).map((row) => row[idKeyLabel])),
	].length;
	const previewGridKey = useMemo(
		() =>
			displayPreviewRows
				.map((row) => `${row.__rowId}:${row.__status}:${row.__note}:${row.__isValid}`)
				.join("||"),
		[displayPreviewRows],
	);

	return (
		<Dialog maxWidth="xl" fullWidth scroll="paper" open={modal} onClose={handleClose} PaperProps={{ sx: { height: "82vh" } }}>
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Typography variant="h6">Upload Final MRP</Typography>
				<IconButton onClick={handleClose}>
					<ClearRounded />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", pt: 1.5, pb: 1.5 }}>
				<Stack spacing={1.25} sx={{ minHeight: 0, flex: 1, overflow: "hidden" }}>
					{previewRows.length === 0 && (
						<Alert severity="info" sx={{ py: 0.5, alignItems: "center" }}>
							Choose upload key (<strong>style_id</strong>, <strong>option_id</strong>, or <strong>style_ref_no</strong>
							), then map columns for the key and <strong>final_mrp</strong>. If multiple color-level keys belong to the
							same style with different MRPs, they will be flagged.
						</Alert>
					)}

					<Box>
						<Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
							<FormControl size="small" sx={{ minWidth: 180 }}>
								<InputLabel id="upload-mrp-id-type-label">Upload By</InputLabel>
								<Select
									labelId="upload-mrp-id-type-label"
									label="Upload By"
									value={idKeyType}
									onChange={(event) => handleIdKeyTypeChange(event.target.value)}
									disabled={loading}>
									<MenuItem value={ID_KEY_OPTIONS.style_id.key}>style_id</MenuItem>
									<MenuItem value={ID_KEY_OPTIONS.option_id.key}>option_id</MenuItem>
									<MenuItem value={ID_KEY_OPTIONS.style_ref_no.key}>style_ref_no</MenuItem>
								</Select>
							</FormControl>
							<Button
								component="label"
								variant="contained"
								color="primary"
								disableElevation
								size="small"
								sx={{ minWidth: 150, textTransform: "none", fontWeight: 600, borderRadius: 2, py: 0.9 }}
								startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <UploadRounded />}>
								Choose CSV / Excel
								<input hidden type="file" accept=".csv,.xlsx" onChange={handleFileChange} disabled={loading} />
							</Button>
							<Button
								variant="outlined"
								color="secondary"
								onClick={handleDownloadFormat}
								disabled={loading}
								size="small"
								startIcon={<FileDownloadRounded />}
								sx={{
									minWidth: 138,
									textTransform: "none",
									fontWeight: 600,
									borderRadius: 2,
									borderStyle: "dashed",
									py: 0.9,
								}}>
								Upload Format
							</Button>
							<Button
								variant="contained"
								color="success"
								disableElevation
								size="small"
								startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshRounded />}
								onClick={handleRefreshRows}
								disabled={loading || !headers.length || idColumnIndex === "" || mrpColumnIndex === ""}
								sx={{ minWidth: 132, textTransform: "none", fontWeight: 600, borderRadius: 2, py: 0.9 }}>
								Preview Rows
							</Button>
						</Stack>
						{fileName && (
							<Typography variant="body2" color="text.secondary" pt={1}>
								{`${fileName} - ${sourceRows.length} rows found`}
							</Typography>
						)}
					</Box>

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							columnGap: 2,
							pt: 1,
						}}>
						{headers.length > 0 && (
							<FormControl size="small" sx={{ minWidth: 220 }}>
								<InputLabel id="upload-mrp-id-column-label">{idKeyLabel} Column</InputLabel>
								<Select
									labelId="upload-mrp-id-column-label"
									label={`${idKeyLabel} Column`}
									value={idColumnIndex}
									onChange={(event) => setIdColumnIndex(event.target.value)}>
									{headers.map((header, index) => (
										<MenuItem key={`${header}-${index}`} value={String(index)}>
											{header}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
						{headers.length > 0 && (
							<FormControl size="small" sx={{ minWidth: 220 }}>
								<InputLabel id="upload-mrp-final-column-label">final_mrp Column</InputLabel>
								<Select
									labelId="upload-mrp-final-column-label"
									label="final_mrp Column"
									value={mrpColumnIndex}
									onChange={(event) => setMrpColumnIndex(event.target.value)}>
									{headers.map((header, index) => (
										<MenuItem key={`${header}-${index}`} value={String(index)}>
											{header}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
					</Box>

					{error && <Alert severity="error">{error}</Alert>}

					<Stack sx={{ minHeight: 0, flex: 1, overflow: "hidden" }}>
						{previewRows.length > 0 && (
							<>
								<Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
									<TanstackDatagrid
										key={`${previewGridKey}-${idKeyLabel}`}
										columns={uploadPreviewColumns}
										pageData={displayPreviewRows}
										setPageData={setPreviewRows}
										getRowId={(row) => row.__rowId}
										disablePagination={true}
										serverSidePagination={false}
										enableHorizontalScroll={true}
										headerHeight={40}
										rowHeight={35}
										processRowUpdate={(newRow) => newRow}
										isCellEditable={({ column }) =>
											!["__upload_status", "__note", "__delete"].includes(column.key)
										}
										children={
											<Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
												<Typography variant="body2" color="text.secondary">
													{`${validCount} unique ${idKeyLabel}${validCount === 1 ? "" : "s"} can be uploaded from ${sourceRows.length} rows`}
												</Typography>
											</Box>
										}
									/>
								</Box>
							</>
						)}
					</Stack>
					{canPublish && (
						<Stack direction="row" spacing={1} alignItems="center">
							<FormControlLabel
								control={
									<Checkbox
										checked={markAsPublished}
										onChange={(event) => setMarkAsPublished(event.target.checked)}
										disabled={loading || !canMarkAsPublished}
									/>
								}
								label="Mark as published"
							/>
							{!canMarkAsPublished && (
								<Typography variant="caption" color="warning.main">
									* Mark as published is enabled only when all uploaded rows are valid.
								</Typography>
							)}
						</Stack>
					)}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button variant="outlined" onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleFormSubmit} disabled={loading || validCount === 0}>
					Save Upload
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default UploadModal;
